/**
 * Tally light.
 *
 * One indicator lamp. Three states, and the third is the one that matters:
 *
 *   armed  — this interlock permits spending
 *   safed  — this interlock prevents spending
 *   unlit  — there is no reading
 *
 * `unlit` is a visible dark slate, not an absent element and not something close
 * to the background. An instrument with no signal must still be legibly present,
 * because "I cannot tell you" is information and an operator needs to see that
 * they are being told nothing. This is the same idea as an unscored pair not
 * rendering as a low score.
 */

export type Posture = 'armed' | 'safed' | 'unlit';
export type LampState = Posture | 'alarm';

export interface TallyLightProps {
  state: LampState;
  /** Screen-reader text. Required: a bare coloured dot conveys nothing. */
  label: string;
}

export function TallyLight({ state, label }: TallyLightProps) {
  return (
    <span className="lamp" data-lit={state} role="img" aria-label={label} />
  );
}

/** A labelled lamp row, as used in the Bot Status interlock grid. */
export function TallyRow({
  state,
  name,
  reading,
}: {
  state: LampState;
  name: string;
  reading: string;
}) {
  return (
    <div className="card row">
      <TallyLight state={state} label={`${name}: ${reading}`} />
      <span className="stack-sm" style={{ gap: 0, minWidth: 0 }}>
        <span className="label">{name}</span>
        <span className="data-sm secondary">{reading}</span>
      </span>
    </div>
  );
}
